import { supabase } from "@/integrations/supabase/client";

const BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

/**
 * Call a Supabase edge function with explicit auth token.
 * Avoids the 401 "Invalid JWT" issue with supabase.functions.invoke().
 */
export async function callEdgeFunction<T = any>(
  functionName: string,
  options?: {
    body?: Record<string, unknown>;
    authHeaderName?: "Authorization" | "x-user-auth";
  }
): Promise<{ data: T | null; error: Error | null }> {
  const { data: sessionData } = await supabase.auth.getSession();
  let accessToken = sessionData?.session?.access_token;
  const authHeaderName = options?.authHeaderName ?? "x-user-auth";

  if (!accessToken) {
    return { data: null, error: new Error("No active session. Please sign in.") };
  }

  const doFetch = async (token: string) => {
    const response = await fetch(`${BASE_URL}/${functionName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        [authHeaderName]: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        ok: false as const,
        status: response.status,
        error: payload?.error || payload?.message || `Function failed (${response.status})`,
      };
    }

    return { ok: true as const, data: payload as T };
  };

  let result = await doFetch(accessToken);

  if (!result.ok && result.status === 401) {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (!refreshError && refreshData.session) {
      accessToken = refreshData.session.access_token;
      result = await doFetch(accessToken);
    }
  }

  if (!result.ok) {
    return { data: null, error: new Error(result.error) };
  }

  return { data: result.data, error: null };
}
